import { Feature, MultiPolygon, Polygon } from '@turf/turf';
import { AuthorizableLAANCAdvisoryProperties } from '../model/advisories/AuthorizableLAANCAdvisoryProperties';
import { IAdvisoryPropertiesV3 } from '../model/advisories/IAdvisoryPropertiesV3';
import { IClassGAdvisoryProperties } from '../model/advisories/IClassGAdvisoryProperties';
import { ILAANCAdvisoryProperties, ILAANCBlockReason } from '../model/advisories/ILAANCAdvisoryProperties';
import { INSUFRAdvisoryProperties } from '../model/advisories/INSUFRAdvisoryProperties';
import { ISubdividableMissionProperties } from '../model/ISubdividableMissionProperties';
import { PersistableMissionFeatureCollection } from '../model/PersistableMissionFeatureCollection';
import * as turf from '@turf/turf';
import moment from 'moment';
import { ILAANCStatusMeta } from '../model/advisories/IAuthorizableLAANCAdvisoryProperties';

export class MissionFeatureCollectionDriver {
    private missionFeatureCollection: PersistableMissionFeatureCollection;
    constructor({ features }: { features: Feature<Polygon | MultiPolygon, IAdvisoryPropertiesV3 | ISubdividableMissionProperties>[]}){
        this.missionFeatureCollection = new PersistableMissionFeatureCollection({ features });
    }
    getFeatureCollection = () => this.missionFeatureCollection;

    addLaancAuthorization = (props: {
        advisoryType?: 'advise' | 'block',
        blockReasons?: ILAANCBlockReason[],
        maxAltitude?: number,
        airspaceIdent?: string,
        shiftLat?: number,
        statusMeta?: ILAANCStatusMeta

        // worth revisiting next time we're in here
        // At first, I wanted status?: IPersistableLAANCStatus, but that didn't work
        // because undefined (as in initialized) is ambiguous with undefined as in `?` as in not passed in
        // so default to authorized
        // so my work around is this boolean
        // "right" solution is probably to modify all consumers to be explicit so default is undefined / initialized
        statusUndefined?: boolean,
    }) => {
        // NOTE: currently only support this getting called once
        const shiftLat = props.shiftLat || 0;

        const properties = new AuthorizableLAANCAdvisoryProperties({
            source: 'uasfm+authorizable',
            operationID: 'MOK123450',
            laancStatus: props.statusUndefined ? undefined : 'authorized',
            uasfmGridIds: ['abc'],
            uasfmGridIdsV2: [{ gridId: 'abc', lastEdited: '2020-06-30' }],
            advisoryType: 'advise',
            airspaceClasses: ['D'],
            airspaceIdent: props.airspaceIdent || 'RAP',
            maxAltitude: 300,
            UASFM_Enabled: ['107-AA', '107-FC', '44809-AA'],
            startTime: moment().toISOString(),
            endTime: moment().add(1, 'hour').toISOString(),
            isLicensed: false,
            ...props
        }, props.statusMeta || { initializedSource: 'user-mission-creation' });

        const laancAuthority: Feature<Polygon, AuthorizableLAANCAdvisoryProperties> = turf.circle([1, 1], 400, { units: 'meters', properties });

        if (shiftLat){
            for (const position of laancAuthority.geometry.coordinates[0]){
                position[1] = position[1] + shiftLat;
            }
        }

        const laancAdvisory: Feature<Polygon, ILAANCAdvisoryProperties> = {
            type: 'Feature',
            geometry: {} as any,
            properties: {
                source: 'uasfm+airspace',
                advisoryType: 'advise',
                AIRSPACE_GLOBAL_ID: '',
                AP_CLASS: 'D',
                blockReasons: [],
                CEILING: props.maxAltitude || 300,
                faaVersion: '',
                GLOBAL_ID: '',
                UASFM_AIRSPACE: 'D',
                UASFM_Enabled: ['107-AA', '107-FC', '44809-AA'],
                UASFM_FAAID: 'RAP',
                UASFM_GLOBAL_ID: '',
                UASFM_LAANC: true,
                LAST_EDIT: '12/7/2017',
                ...props
            }
        };

        this.missionFeatureCollection.features.push(laancAuthority);
        this.missionFeatureCollection.features.push(laancAdvisory);
    }

    addUncontrolled = () => {
        const uncontrolled: Feature<Polygon, IClassGAdvisoryProperties> = {
            type: 'Feature',
            properties: {
                source: 'airspace+class-g',
                advisoryType: 'advise',
                AP_CLASS: 'G'
            },
            geometry: {} as any
        };
        this.missionFeatureCollection.features.push(uncontrolled);
    }

    addNSUFR = () => {
        const nsufr: Feature<Polygon, INSUFRAdvisoryProperties> = {
            type: 'Feature',
            geometry: {} as any,
            properties: {
                source: 'nsufr',
                advisoryType: 'block',
                Airspace: '',
                Base: '',
                Branch: '',
                Ceiling: '',
                County: '',
                FAA_ID: 'RAP',
                Facility: '',
                Floor: '',
                GLOBAL_ID: '',
                OBJECTID: 1,
                POC: '',
                Proponent: '',
                Reason: '',
                State: ''
            }
        };
        this.missionFeatureCollection.features.push(nsufr);
    }
}
